import React, { useRef, useCallback } from "react";
import { t } from "../i18n";
import { ArrowLeft, RectangleHorizontal } from "lucide-react";
import Sidebar from "../components/Sidebar.jsx";
import VideoTimeline from "../components/VideoTimeline.jsx";
import VideoPreview from "../components/VideoPreview.jsx";
import TransitionEditor from "../components/TransitionEditor.jsx";
import MontageEditor from "../components/MontageEditor.jsx";
import ToastContainer from "../components/ToastContainer.jsx";
import useMontage from "../hooks/useMontage.js";
import usePlayback from "../hooks/usePlayback.js";
import useSegmentSelection from "../hooks/useSegmentSelection.js";
import MontageList from "./montage/MontageList.jsx";
import MontageCreator from "./montage/MontageCreator.jsx";
import StoryboardStep, { StoryboardBrief } from "./montage/StoryboardStep.jsx";
import ImagesStep from "./montage/ImagesStep.jsx";
import VideosStep from "./montage/VideosStep.jsx";
import "./VideoMontage.css";

// Étapes du workflow
function getWorkflowSteps() {
  return [
    { id: "storyboard", label: `1. ${t("montage.step.storyboard")}`, icon: "📝" },
    { id: "images", label: `2. ${t("montage.step.images")}`, icon: "🖼️" },
    { id: "videos", label: `3. ${t("montage.step.videos")}`, icon: "🎥" },
    { id: "transitions", label: `4. ${t("montage.step.transitions")}`, icon: "🔄" },
    { id: "music", label: `5. ${t("montage.step.music")}`, icon: "🎵" },
    { id: "assemble", label: `6. ${t("montage.step.assemble")}`, icon: "🎬" }
  ];
}

export default function VideoMontage() {
  // Toast ref
  const toastRef = useRef(null);
  const toast = useCallback((msg, type = "info") => {
    toastRef.current?.show(msg, type);
  }, []);

  // ─── Custom hooks ───
  const m = useMontage(toast);
  const playback = usePlayback({
    montage: m.montage,
    setMontage: m.setMontage,
    lastSavedRef: m.lastSavedRef,
    toast,
  });
  const selection = useSegmentSelection(m.montage.json_data.segments);

  // ─── Drag & drop handlers object for StoryboardStep ───
  const dragHandlers = {
    onDragStart: m.handleStoryboardDragStart,
    onDragOver: m.handleStoryboardDragOver,
    onDragLeave: m.handleStoryboardDragLeave,
    onDrop: m.handleStoryboardDrop,
    onDragEnd: m.handleStoryboardDragEnd,
    draggedIndex: m.draggedSegmentIndex,
    dragOverIndex: m.dragOverIndex,
  };

  // ─── Wrapped generation handlers (inject selection) ───
  const handleGenerateImagesWithSelection = useCallback((segmentIdsOverride = null, targets = []) => {
    m.handleGenerateImages(segmentIdsOverride, targets, selection.selectedSegments);
    selection.setSelectedSegments([]);
  }, [m.handleGenerateImages, selection.selectedSegments, selection.setSelectedSegments]);

  const handleGenerateVideosWithSelection = useCallback((segmentIdsOverride = null) => {
    m.handleGenerateVideos(segmentIdsOverride, selection.selectedVideoSegments);
    selection.setSelectedVideoSegments([]);
  }, [m.handleGenerateVideos, selection.selectedVideoSegments, selection.setSelectedVideoSegments]);

  // ─── Render step content ───
  const renderStepContent = () => {
    switch (m.currentStep) {
      case "storyboard":
        return (
          <StoryboardStep
            montage={m.montage}
            loading={m.loading}
            enhancingPrompts={m.enhancingPrompts}
            onUpdate={m.updateMontage}
            onGenerateStoryboard={m.handleGenerateStoryboard}
            onUpdateSegment={m.handleUpdateSegment}
            onDeleteSegment={m.handleDeleteSegment}
            onRegenerateSegment={m.handleRegenerateSegment}
            onEnhanceSegmentPrompt={m.handleEnhanceSegmentPrompt}
            onEnhanceAllPrompts={m.handleEnhanceAllPrompts}
            onAddSegment={m.handleAddSegment}
            onSetStep={m.setCurrentStep}
            onReferenceChange={m.handleReferenceChange}
            dragHandlers={dragHandlers}
          />
        );

      case "images":
        return (
          <ImagesStep
            segments={m.montage.json_data.segments || []}
            selectedSegments={selection.selectedSegments}
            imageQuality={m.imageQuality}
            generatingImages={m.generatingImages}
            onSetImageQuality={m.setImageQuality}
            onToggleSegment={selection.toggleSegmentSelection}
            onToggleAll={selection.toggleAllSegments}
            onGenerate={handleGenerateImagesWithSelection}
            onSetStep={m.setCurrentStep}
          />
        );

      case "videos":
        return (
          <VideosStep
            segments={m.montage.json_data.segments || []}
            selectedVideoSegments={selection.selectedVideoSegments}
            videoQuality={m.videoQuality}
            generatingVideos={m.generatingVideos}
            onSetVideoQuality={m.setVideoQuality}
            onToggleSegment={selection.toggleVideoSegmentSelection}
            onToggleAll={selection.toggleAllVideoSegments}
            onGenerate={handleGenerateVideosWithSelection}
            onSetStep={m.setCurrentStep}
          />
        );

      case "transitions": {
        const transitionSegments = m.montage.json_data.segments || [];
        const hasTransitionSegments = transitionSegments.length > 0;
        const allVideosReady = transitionSegments.length > 0 && transitionSegments.every(seg =>
          seg.video?.status === "completed"
        );

        return (
          <div className="montage-step-content">
            <h2>🔄 {t("montage.transitions.title")}</h2>
            <p className="montage-step-description">{t("montage.transitions.desc")}</p>

            {!hasTransitionSegments ? (
              <div className="montage-empty-state">
                <p>{t("montage.warn.noSegments")}</p>
                <button type="button" className="montage-btn montage-btn-secondary" onClick={() => m.setCurrentStep("storyboard")}>
                  {t("montage.btn.backStoryboard")}
                </button>
              </div>
            ) : !allVideosReady ? (
              <div className="montage-empty-state">
                <p>{t("montage.warn.videosFirst")}</p>
                <button type="button" className="montage-btn montage-btn-secondary" onClick={() => m.setCurrentStep("videos")}>
                  {t("montage.btn.backVideos")}
                </button>
              </div>
            ) : (
              <>
                <div className="montage-timeline-section">
                  <h3>{t("montage.label.preview")}</h3>
                  <VideoPreview
                    segments={transitionSegments}
                    currentTimeMs={playback.currentTimeMs}
                    isPlaying={playback.isPlaying}
                    onTimeUpdate={playback.setCurrentTimeMs}
                    onPlayPause={playback.handlePlayPause}
                    onSeek={playback.handleSeek}
                    aspectRatio={m.montage.aspect_ratio}
                    previewUrl={playback.previewUrl}
                    previewStatus={playback.previewStatus}
                    previewLoading={playback.previewLoading}
                  />
                </div>

                <div className="montage-timeline-section">
                  <h3>{t("montage.label.timeline")}</h3>
                  <VideoTimeline
                    segments={transitionSegments}
                    currentTimeMs={playback.currentTimeMs}
                    timelineData={playback.timelineData}
                    onSeek={playback.handleSeek}
                    onSegmentReorder={playback.handleSegmentReorder}
                    onSegmentClick={playback.handleSegmentClick}
                    onTransitionClick={playback.handleTransitionClick}
                    activeSegmentId={playback.activeSegmentId}
                  />
                </div>

                {playback.editingTransition && (
                  <div className="transition-editor-overlay">
                    <TransitionEditor
                      fromSegment={transitionSegments[playback.editingTransition.fromIndex]}
                      toSegment={transitionSegments[playback.editingTransition.toIndex]}
                      onSave={playback.handleSaveTransition}
                      onClose={() => playback.setEditingTransition(null)}
                    />
                  </div>
                )}

                <div className="montage-timeline-instructions">
                  <h4>{t("montage.instructions.title")}</h4>
                  <ul>
                    <li>{t("montage.instructions.click")}</li>
                    <li>{t("montage.instructions.drag")}</li>
                    <li>{t("montage.instructions.transitions")}</li>
                    <li>{t("montage.instructions.playback")}</li>
                  </ul>
                </div>

                <div className="montage-segments-actions">
                  <button type="button" className="montage-btn montage-btn-secondary" onClick={() => m.setCurrentStep("music")}>
                    {t("montage.btn.nextMusic")}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      }

      case "music":
        return (
          <div className="montage-step-content">
            <h2>🎵 {t("montage.music.title")}</h2>
            <p className="montage-step-description">{t("montage.music.desc")}</p>
            <p className="montage-placeholder">Composant MusicGenerator à implémenter</p>
          </div>
        );

      case "assemble":
        return (
          <div className="montage-step-content">
            <h2>🎬 {t("montage.assemble.title")}</h2>
            <p className="montage-step-description">{t("montage.assemble.desc")}</p>
            <p className="montage-placeholder">Composant AssemblyPanel à implémenter</p>
          </div>
        );

      default:
        return null;
    }
  };

  // ─── Layout logic ───
  const hasSegments = m.montage.json_data.segments.length > 0;
  const showEditor = m.montage.montage_id && hasSegments;

  return (
    <div className="app-shell">
      <Sidebar active="montage" />

      <div className={`app-main ${showEditor ? "app-main-full" : ""}`}>
        {/* ===== EDITOR MODE: Timeline Orchestrator ===== */}
        {showEditor ? (
          <div className="montage-editor-wrapper">
            {/* Compact header bar */}
            <div className="montage-editor-topbar">
              <div className="montage-editor-topbar-left">
                <button
                  type="button"
                  className="montage-editor-back-btn"
                  onClick={m.resetMontage}
                  title={t("montage.btn.back")}
                >
                  <ArrowLeft size={20} />
                </button>
                <span className="montage-editor-topbar-title">{m.montage.title}</span>
                <span className="montage-editor-topbar-meta">
                  {m.montage.aspect_ratio} &middot; {m.montage.resolution}
                  &middot; {m.montage.json_data.segments.length} seg.
                  {m.montage.json_data.total_duration > 0 &&
                    ` \u00b7 ${m.montage.json_data.total_duration}s`}
                </span>
              </div>
              <div className="montage-editor-topbar-right">
                <span className="montage-format-pill">
                  <RectangleHorizontal size={16} />
                  {(() => {
                    const ratio = m.montage.json_data?.aspect_ratio || m.montage.aspect_ratio || "";
                    if (!ratio) return t("montage.format.undefined");
                    if (ratio === "9:16") return `${t("montage.format.portrait")} ${ratio}`;
                    if (ratio === "16:9") return `${t("montage.format.landscape")} ${ratio}`;
                    if (ratio === "1:1") return `${t("montage.format.square")} ${ratio}`;
                    return `${t("montage.label.format")} ${ratio}`;
                  })()}
                  {(() => {
                    const res = m.montage.json_data?.resolution || m.montage.resolution || "";
                    return res ? ` · ${res}` : "";
                  })()}
                </span>
                <span className="montage-autosave-info">
                  {m.saving ? t("montage.saving") : "\u2713 Auto-save"}
                </span>
              </div>
            </div>

            {m.error && (
              <div className="montage-error">
                <span>{m.error}</span>
                <button onClick={() => m.setError("")}>&times;</button>
              </div>
            )}

            {m.montage.json_data?.export_error && (
              <div className="montage-error">
                <span>{t("montage.err.exportFailed")} {m.montage.json_data.export_error}</span>
                <button onClick={() => {
                  m.setMontage(prev => ({
                    ...prev,
                    json_data: { ...prev.json_data, export_error: null, status: "draft" }
                  }));
                }}>&times;</button>
              </div>
            )}

            {/* Timeline Orchestrator layout */}
            <MontageEditor
              montage={m.montage}
              segments={m.montage.json_data.segments}
              referenceMedia={m.montage.json_data.reference_media || []}
              onUpdateReferences={m.handleReferenceChange}
              referencePickerTarget="montage_ref"
              referencePickerReturn={m.montage.montage_id ? `/montage?id=${m.montage.montage_id}` : "/montage"}
              currentTimeMs={playback.currentTimeMs}
              isPlaying={playback.isPlaying}
              activeSegmentId={playback.activeSegmentId}
              onPlayPause={playback.handlePlayPause}
              onSeek={playback.handleSeek}
              onTimeUpdate={playback.setCurrentTimeMs}
              previewUrl={playback.previewUrl}
              previewStatus={playback.previewStatus}
              previewLoading={playback.previewLoading}
              onSegmentClick={playback.handleSegmentClick}
              onSegmentReorder={playback.handleSegmentReorder}
              onUpdateSegment={m.handleUpdateSegment}
              onSaveSegment={m.handleSaveSegment}
              onAddReferenceFromSegment={m.handleAddReferenceFromSegment}
              onDeleteSegment={m.handleDeleteSegment}
              onAddSegment={m.handleAddSegment}
              onRegenerateSegment={m.handleRegenerateSegment}
              onEnhanceSegmentPrompt={m.handleEnhanceSegmentPrompt}
              onSaveTransition={playback.handleSaveTransition}
              onSave={() => m.handleSave(false)}
              onEnhanceAll={m.handleEnhanceAllPrompts}
              onGenerateImages={handleGenerateImagesWithSelection}
              onGenerateVideos={handleGenerateVideosWithSelection}
              imageQuality={m.imageQuality}
              videoQuality={m.videoQuality}
              onSetImageQuality={m.setImageQuality}
              onSetVideoQuality={m.setVideoQuality}
              saving={m.saving}
              loading={m.loading}
              enhancingPrompts={m.enhancingPrompts}
              generatingImages={m.generatingImages}
              generatingVideos={m.generatingVideos}
              exporting={m.exporting}
              onExport={m.handleExport}
              harmonizing={m.harmonizing}
              onHarmonize={m.handleHarmonize}
            />
          </div>
        ) : (
          /* ===== BRIEF MODE: Creation + Storyboard ===== */
          <div className="montage-container">
            <header className="montage-header">
              <div>
                <p className="montage-kicker">{t("montage.kicker")}</p>
                <h1 className="montage-title page-title-accent">{t("montage.title")}</h1>
                <p className="montage-subtitle">{t("montage.subtitle")}</p>
              </div>
            </header>

            {m.error && (
              <div className="montage-error">
                <span>{m.error}</span>
                <button onClick={() => m.setError("")}>&times;</button>
              </div>
            )}

            <MontageList
              montagesList={m.montagesList}
              activeMontageId={m.montage.montage_id}
              onLoad={m.handleLoad}
              onDelete={m.handleDelete}
              onNew={m.resetMontage}
            />

            {!m.montage.montage_id && (
              <MontageCreator
                montage={m.montage}
                loading={m.loading}
                onUpdate={m.updateMontage}
                onCreate={m.handleCreate}
              />
            )}

            {m.montage.montage_id && !hasSegments && (
              <StoryboardBrief
                montage={m.montage}
                loading={m.loading}
                onUpdate={m.updateMontage}
                onGenerateStoryboard={m.handleGenerateStoryboard}
              />
            )}
          </div>
        )}
      </div>

      <ToastContainer ref={toastRef} />
    </div>
  );
}
